import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Avatar,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { CheckCircle, HourglassEmpty, Cancel, Close } from "@mui/icons-material";
import { API_GET_TASK_HISTORY } from "../../../Util/URLConstants";

function TaskHistory({ id }) {
  // const sessionFactory = new StorageFactory("session");
  // const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  // Track broken images by task id
  const [brokenImages, setBrokenImages] = useState({});

  const statusConfig = {
    inprogress: { color: "info", icon: <HourglassEmpty fontSize="small" /> },
    complete: { color: "success", icon: <CheckCircle fontSize="small" /> },
    incomplete: { color: "error", icon: <Cancel fontSize="small" /> },
  };

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API_GET_TASK_HISTORY}?id=${id}`);
        setHistory(res.data.data || []);
      } catch (err) {
        console.error("Error fetching task history:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No task history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={2}>
        {history.map((task) => {
          const statusInfo = statusConfig[task.task_status] || {};
          const hideImage = brokenImages[task.id]; // check if broken

          return (
            <Card
              key={task.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: "0.3s",
                "&:hover": { boxShadow: 4, borderColor: "primary.main" },
              }}
            >
              <CardContent>
                {/* Header row */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1.5}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: `${statusInfo.color}.light`,
                        width: 32,
                        height: 32,
                      }}
                    >
                      {statusInfo.icon}
                    </Avatar>
                    <Chip
                      label={task.task_status?.toUpperCase()}
                      color={statusInfo.color || "default"}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {new Date(task.created_date).toLocaleString()}
                  </Typography>
                </Stack>

                {/* Comments */}
                {task.comments && (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    mb={1.5}
                    sx={{ whiteSpace: "pre-line" }}
                  >
                    {task.comments}
                  </Typography>
                )}

                {/* Image (only show if not broken) */}
                {task.image_url && !hideImage && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box mt={1}>
                      <img
                        src={task.image_url}
                        alt="Task"
                        loading="lazy"
                        onClick={() => setPreviewImage(task.image_url)}
                        onError={() =>
                          setBrokenImages((prev) => ({
                            ...prev,
                            [task.id]: true,
                          }))
                        }
                        style={{
                          width: "100%",
                          maxWidth: "220px",
                          height: "140px",
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #ddd",
                          cursor: "pointer",
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                        textAlign="center"
                      >
                        Click image to preview
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Image Preview Dialog */}
      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.95)",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ position: "relative", p: 0, height: "100vh", overflow: "hidden" }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{ position: "absolute", top: 12, right: 12, zIndex: 10, color: "#fff", bgcolor: "rgba(0,0,0,0.4)" }}
          >
            <Close />
          </IconButton>
          {previewImage && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ width: "100vw", height: "100vh", overflow: "hidden" }}
            >
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: "100vw",
                  height: "100vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TaskHistory;
